// Click-to-sort column header. Three-state cycle on click:
//   inactive  → asc   (↕ → ↑)
//   asc       → desc  (↑ → ↓)
//   desc      → off   (↓ → ↕, back to page default)
//
// The page owns the sort state and the comparator function; this
// component is only UI. Used by AoiDetail roster + PoolOrderDetail
// matched/suggested/force-pool tabs.

export type SortDir = 'asc' | 'desc';

interface SortHeaderProps<F extends string> {
  label: string;
  field: F;
  active: F | null;
  dir: SortDir;
  onChange: (field: F | null, dir: SortDir) => void;
  align?: 'left' | 'center' | 'right';
}

export function SortHeader<F extends string>({
  label, field, active, dir, onChange, align = 'left',
}: SortHeaderProps<F>) {
  const isActive = active === field;
  const arrow = !isActive ? '↕' : dir === 'asc' ? '↑' : '↓';
  const alignCls = align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start';
  function click() {
    if (!isActive)          onChange(field, 'asc');
    else if (dir === 'asc') onChange(field, 'desc');
    else                    onChange(null, 'asc');
  }
  return (
    <button onClick={click}
      className={
        'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono w-full ' +
        alignCls + ' ' +
        (isActive ? 'text-accent-bright' : 'text-text-faint hover:text-text')
      }>
      {label}
      <span className={'font-mono text-[9px] ' + (isActive ? '' : 'opacity-60')}>{arrow}</span>
    </button>
  );
}

// ── Sort comparators ──────────────────────────────────────────────
// All three pages need name / entity_type / phone sort. Phone is a
// bool — true (has phone) sorts before false in asc direction since
// "has phone" is operationally more valuable. Entity type sorts
// alphabetically within type, with nulls last.

export function compareStrings(a: string | null | undefined, b: string | null | undefined, dir: SortDir): number {
  // Null/empty go LAST regardless of direction.
  const aEmpty = !a;
  const bEmpty = !b;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const cmp = a!.toLowerCase().localeCompare(b!.toLowerCase());
  return dir === 'asc' ? cmp : -cmp;
}

export function compareBool(a: boolean, b: boolean, dir: SortDir): number {
  // asc = "has it first" (true before false). desc = "missing first".
  if (a === b) return 0;
  const aFirst = dir === 'asc' ? a : !a;
  return aFirst ? -1 : 1;
}
